import React, { FC, useEffect, useState, useMemo } from "react"
import { Button, Form, Input, Modal, Table, TablePaginationConfig, Upload, Alert } from "antd"
import { useStore } from "stores/utils/use-store"
import { observer } from "mobx-react"

import { codesUploadColumns } from "./codes-columns"
import { DeleteIconButton, showDeleteConfirm, SubmitCancelButtons } from "components"
import { UploadOutlined } from "@ant-design/icons"
import { ValidateErrorEntity } from "rc-field-form/lib/interface"
import { SubCodesInventoryType } from "../../store/types"

export const CodeUpload: FC<{
  closeModal: () => void;
}> = observer(() => {
  const { dataStore: { subCodesStore, codesStore } } = useStore()

  const [uploadOneCode, setuploadOneCode] = useState(false)
  const [uploadMultiple, setuploadMultiple] = useState(false)

  const [fileToUpload, setFileToUpload] = useState<FormData>()
  const [filelist, setfilelist] = useState<any>([])

  useEffect(() => {
    if (codesStore?.selectedElement?.id) {
      subCodesStore.getSubCodes(codesStore.selectedElement.id)
    }
  }, [codesStore, subCodesStore])

  const columns = [
    ...codesUploadColumns,
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_: unknown, row: SubCodesInventoryType) => (
        <div style={{ display: "flex", gap: 10 }}>
          <DeleteIconButton
            onClick={e => {
              e.stopPropagation()
              showDeleteConfirm(row.code, undefined, async() => {
                subCodesStore.funcAfterSuccess = async() => await subCodesStore.getSubCodes(codesStore?.selectedElement?.id || "")
                await subCodesStore.deleteCode(row.id)
              })
            }}
          />
        </div>
      ),
    },
  ]
  const onFinishHandler = async(e: any) => {
    if (codesStore?.selectedElement?.id) {
      subCodesStore.funcAfterSuccess = () => {
        setuploadOneCode(false)
        setTimeout(() => {
          subCodesStore.getSubCodes(codesStore?.selectedElement?.id || "")
        }, 2000)
      }

      subCodesStore.createSubCodes({
        codeInfoId: codesStore?.selectedElement?.id,
        codes: [e.code],
      })
    }
  }

  const onCsvSubmit = async() => {
    await subCodesStore.createSubCodesCsv(
      codesStore?.selectedElement?.id as string,
      fileToUpload as FormData,
    )
    subCodesStore.getSubCodes(codesStore?.selectedElement?.id || "")
    console.log(fileToUpload, "fileToUpload")
    setFileToUpload(undefined)
    setfilelist([])
    setuploadMultiple(false)
  }

  const onFinishFailedHandler = (e: ValidateErrorEntity<any>) => {
    console.log(e)
  }

  const sortedCodes = useMemo(() => {
    const codes = subCodesStore.subCodesInventory || []

    return [...codes].sort((a: SubCodesInventoryType, b: SubCodesInventoryType) => {
      const aHasAssignee = a.accountId != null && a.accountId !== ""
      const bHasAssignee = b.accountId != null && b.accountId !== ""

      if (aHasAssignee === bHasAssignee) {
        return 0
      }

      return aHasAssignee ? 1 : -1
    })
  }, [subCodesStore.subCodesInventory])

  const { duplicateCodes, duplicateCount } = useMemo(() => {
    const codeCounts = new Map<string, number>()
    const codes = sortedCodes

    codes.forEach((item: SubCodesInventoryType) => {
      const code = item.code
      codeCounts.set(code, (codeCounts.get(code) || 0) + 1)
    })

    const duplicates = new Set<string>()
    codeCounts.forEach((count, code) => {
      if (count > 1) {
        duplicates.add(code)
      }
    })

    const totalDuplicates = codes.filter((item: SubCodesInventoryType) => duplicates.has(item.code)).length

    return {
      duplicateCodes: duplicates,
      duplicateCount: totalDuplicates,
    }
  }, [sortedCodes])

  const paginationOptionsCodes: TablePaginationConfig = {
    onChange: async(page, pageSize) => {
      subCodesStore.pagination = {
        page,
        limit: pageSize || subCodesStore.pagination.limit,
      }
      if (codesStore?.selectedElement?.id) {
        await subCodesStore.getSubCodes(codesStore.selectedElement.id)
      }
    },
    total: subCodesStore.total,
    showTotal: (total, range) => `${range[0]} to ${range[1]} of ${total}`,
    current: subCodesStore.pagination.page,
    pageSize: subCodesStore.pagination.limit,
    showSizeChanger: true,
    pageSizeOptions: ["50", "100", "250", "500", "1000"],
  }

  return (
    <>
      <div style={{ display: "flex", gap: 10, paddingTop: "15px" }}>
        <Button
          type="primary"
          onClick={() => setuploadOneCode(true)}
          style={{ marginBottom: "1rem" }}
        >
          Upload a new code
        </Button>
        <Button
          type="primary"
          onClick={() => setuploadMultiple(true)}
          style={{ marginBottom: "1rem" }}
        >
          Upload via CSV
        </Button>
      </div>
      {duplicateCount > 0 && (
        <Alert
          message={`Found ${duplicateCount} row${duplicateCount > 1 ? "s" : ""} with duplicate codes in the table`}
          type="warning"
          showIcon
          style={{ marginBottom: "1rem" }}
        />
      )}
      <Table
        dataSource={sortedCodes}
        columns={columns as any}
        rowKey="id"
        pagination={paginationOptionsCodes}
        loading={subCodesStore.isLoading}
        id="raffleTable"
        onRow={(record: SubCodesInventoryType) => ({
          style: duplicateCodes.has(record.code)
            ? {
              backgroundColor: "#ffcdd2",
              borderLeft: "4px solid #d32f2f",
              fontWeight: 500,
            }
            : undefined,
        })}
      />
      {uploadMultiple && (
        <Modal
          visible={uploadMultiple}
          onCancel={() => setuploadMultiple(false)}
          width={720}
          bodyStyle={{ paddingBottom: 20, paddingTop: 50 }}
          footer={null}
        >
          <Upload
            name="file"
            beforeUpload={file => {
              const formData = new FormData()
              formData.append("file", file)

              setFileToUpload(formData)
              setfilelist([file])

              return false
            }}
            maxCount={1}
            fileList={filelist}
            onRemove={() => {
              setfilelist([])
              setFileToUpload(undefined)
            }}
          >
            <Button icon={<UploadOutlined />}>Click to Add file</Button>
          </Upload>
          <Button
            disabled={!fileToUpload || subCodesStore.isLoading}
            loading={subCodesStore.isLoading}
            onClick={onCsvSubmit}
          >
            Upload Selected File?
          </Button>
        </Modal>
      )}
      {uploadOneCode && (
        <Modal
          visible={uploadOneCode}
          onCancel={() => setuploadOneCode(false)}
          width={720}
          bodyStyle={{ paddingBottom: 20, paddingTop: 50 }}
          footer={null}
        >
          <Form onFinish={onFinishHandler} onFinishFailed={onFinishFailedHandler} id="createBanner">
            <Form.Item label="Code" name="code">
              <Input />
            </Form.Item>

            <SubmitCancelButtons
              onClose={() => setuploadOneCode(false)}
              isLoading={subCodesStore.isLoading}
            />
          </Form>
        </Modal>
      )}
    </>
  )
})
